import {
  faArrowLeft,
  faArrowRight,
  faCheckCircle,
  faChevronRight,
  faTimesCircle,
} from '@fortawesome/pro-regular-svg-icons'
import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { ProductCard } from '../../../components/ProductCard/ProductCard'
import { ProductCardRateType } from '../../../components/ProductCard/ProductCardHeader'
import {
  ProductCardFeatureSpan,
  ProductCardFeatureText,
  ProductCardIcon,
} from '../../../components/ProductCard/styledProductCardFeature'
import {
  ProductCardTable,
  ProductCardTableBody,
  ProductCardTableIcon,
  ProductCardTableRow,
  ProductCardTableText,
  ProductTableKeyCell,
  ProductTableValueCell,
} from '../../../components/ProductCard/styledProductCardTable'
import { ProductCardCarousel } from '../../../components/ProductCardCarousel/ProductCardCarousel'
import { Link } from '../../../components/link/link'
import { DisclaimerContext } from '../../../context/disclaimerContext'
import { DisclaimersFragment, FcpProductComparisonTableFragment, ProductComparisonFragment } from '@gql'
import { Maybe } from '../../../gql'
import { isNotNull, nonNull } from '../../../utils/nonNull'
import { StyledButton } from '../styledProductListing'
import { Typography } from '@material-ui/core'
import { irem } from '../../../utils'
import { ArrowNavButton, Button } from '../../../components/button/button'
import FlickityInstance from 'flickity'
import { FlickityRefObject } from '../../../types/FlickityRefObject'
import { Icon } from '../../../components/icon/icon'

type FCPProductComparisonFragment = FcpProductComparisonTableFragment['productComparisonTable'][number]

export type ProductComparisonProps = {
  data: ProductComparisonFragment[] | FCPProductComparisonFragment[]
  title?: React.ReactNode
}

const StyledWrapper = styled.div`
  position: relative;
`

const StyledHeader = styled.header`
    text-align: center;
    
    h5 {
        margin-bottom: ${irem(30)};
    }
    
    ${props => props.theme.breakpoints.up('sm')} {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        text-align: left;
        height: ${irem(50)};
        margin-bottom: ${irem(50)};
        
        h5 {
            margin: 0;
        }
    }
`

const StyledInner = styled.div``

const StyledButtonWrapper = styled.div`
    margin-left: auto;
    display: none;
    
    ${props => props.theme.breakpoints.up('sm')} {
        display: block;
    }
`


export const ProductComparison: React.FC<PropsWithChildren<ProductComparisonProps>> = ({ data, title }) => {
  const [isShowDetail, setShowDetail] = useState(false)
  const flickityRef = useRef() as FlickityRefObject
  const onCardExpanded = () => {
    setShowDetail(!isShowDetail)
  }

  const { pageId } = useContext(DisclaimerContext)

  const changeSlide = useCallback((direction: 'left' | 'right') => {
    if (flickityRef.current?.flkty) {
      flickityRef.current.flkty[direction === 'left' ? 'previous' : 'next']()
    }
  }, [])

  if (!data) {
    return null
  }

  return (
    <StyledWrapper>
      <StyledHeader>
        {title && (
          <Typography variant={'h5'}>{title}</Typography>
        )}

        {data.length > 2 && (
          <StyledButtonWrapper>
            <ArrowNavButton onClick={() => changeSlide('left')}>
              <Icon icon={faArrowLeft} />
            </ArrowNavButton>
            <ArrowNavButton onClick={() => changeSlide('right')}>
              <Icon icon={faArrowRight} />
            </ArrowNavButton>
          </StyledButtonWrapper>
        )}
      </StyledHeader>
      <StyledInner>
        <ProductCardCarousel expanded={isShowDetail} flickityRef={flickityRef}>
          {nonNull<ProductComparisonFragment | FCPProductComparisonFragment>(data).map((item, index) => (
            <ProductCard wrapper key={index}>
              {isNotNull(item.productRate) && isNotNull(item.productRate[0]) && (item.productRate[0].__typename === 'rates_default_Entry' || item.productRate[0].__typename === 'productRatesCustom_default_Entry') ? (
                <ProductCard header heading={item.productTitle? item.productTitle: ''} href={item?.learnMoreLink?.url || '#'} rates={item.productRate[0] as ProductCardRateType}>
                  {item.productDescription}
                  { item.productRate[0] && item.productRate[0].__typename === 'rates_default_Entry' &&
                    <Link href={`#disclaimer_block_${pageId}`} passHref>
                      <StyledDiscliamerLink>
                        <DisclaimerIcons disclaimers={[ ...(item.productRate[0]?.homeLoanDisclaimers ?? []), ...(item.productRate[0]?.personalLoanDisclaimers ?? []) ]} />
                      </StyledDiscliamerLink>
                    </Link>
                  }
                </ProductCard>
              ) : (
                <ProductCard header heading={item.productTitle? item.productTitle: ''} href={item?.learnMoreLink?.url || '#'}>
                  {item.productDescription}
                </ProductCard>
              )}
              <ProductCard feature>
                {isNotNull(item.productBenefits) && item.productBenefits.map((productData, index) => {return isNotNull(productData) && productData.__typename == 'productBenefits_TableRow' ?(
                  <ProductCardFeatureSpan key={index}>
                    <ProductCardIcon icon={faCheckCircle}/>
                    <ProductCardFeatureText>
                      {productData.benefit}
                    </ProductCardFeatureText>
                  </ProductCardFeatureSpan>
                ): null })}
              </ProductCard>
              <ProductCard detail expanded={isShowDetail} onDetailExpand={onCardExpanded} enabled={(item.productComparisonTable?.length ?? 0) > 0}>
                <ProductCardTable>
                  <ProductCardTableBody>
                    {item.productComparisonTable && item.productComparisonTable.map((item, index) => {
                      if(isNotNull(item)&& item.__typename == 'productComparisonTable_comparisonField_BlockType') {
                        const { fieldTitle, fieldText, fieldIcon } = item

                        return (
                          <ProductCardTableRow key={index}>
                            <ProductTableKeyCell component="th" scope="row">
                              {fieldTitle}
                            </ProductTableKeyCell>
                            <ProductTableValueCell>
                              {fieldIcon !== 'unset' ? <ProductCardTableIcon icon={fieldIcon == 'tick' ? faCheckCircle : faTimesCircle} /> : null}
                              {fieldText && <ProductCardTableText>{fieldText}</ProductCardTableText>}
                            </ProductTableValueCell>
                          </ProductCardTableRow>
                        )
                      } else {
                        return null
                      }})}
                  </ProductCardTableBody>
                </ProductCardTable>
              </ProductCard>
              <ProductCard footer>
                <StyledButton target="_blank" variant={'outlined'} href={item?.buttonLink?.url || '#'}>
                Apply now
                </StyledButton>
                <StyledTextButton href={item?.learnMoreLink?.url || '#'} icon={faChevronRight}>
                  Learn more
                </StyledTextButton>
              </ProductCard>
            </ProductCard>
          ))}
        </ProductCardCarousel>
      </StyledInner>
    </StyledWrapper>
  )
}

const StyledTextButton = styled(Button).attrs({ text: true })`
  text-decoration: none;
  color: ${props => props.theme.palette.text.primary};
`

export const StyledIcon = styled.span`
  & + &::before {
    content: ' ';
  }
`
export const StyledDiscliamerLink = styled.a`
  text-decoration: none;
  color: ${props => props.theme.palette.primary.main};
  vertical-align: super;
  font-size: 0.65em;
  font-weight: 500;
  padding-left: 3px;
`

export const DisclaimerIcons: React.VFC<{ disclaimers: Maybe<DisclaimersFragment>[] }> = ({ disclaimers }) => {
  const { iconMap } = useContext(DisclaimerContext)

  const icons = nonNull(disclaimers).map(d => {
    if ((d.__typename === 'homeLoanDisclaimers_default_Entry' || d.__typename === 'personalloandisclaimers_default_Entry') && d.disclaimerId && iconMap.has(d.disclaimerId)) {
      return (
        <StyledIcon key={d.disclaimerId}>
          {iconMap.get(d.disclaimerId)}
        </StyledIcon>
      )
    }

    return null
  }).filter(isNotNull)

  return (
    <>
      {icons}
    </>
  )
}
